// screens/supplier/SupplierAddProduct.js
import React, { useState, useEffect } from "react";
import { Upload, X, Plus, Save, ArrowLeft, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { uploadToCloudinary } from "../../utils/cloudinary";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

// ── Safely extract display string from plain string OR {en,hi,es,zh} ──
const getText = (field, lang = "en") => {
  if (!field) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object") {
    return field[lang] || field["en"] || Object.values(field)[0] || "";
  }
  return String(field);
};

// ── Upload video to Cloudinary ────────────────────────────────────────
const uploadVideoToCloudinary = async (file) => {
  if (!file) throw new Error("No video selected");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);
  formData.append("resource_type", "video");
  const { data } = await axios.post(
    `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/video/upload`,
    formData
  );
  return data;
};

// ── Wrap a plain string into the multilingual schema shape ────────────
// Schema: { en: String (required), hi, es, zh (optional) }
const ml = (value) => ({ en: value || "" });

// ── Generate a unique bhkg_id ─────────────────────────────────────────
const generateBhkgId = () =>
  "BHKG-" +
  Date.now().toString(36).toUpperCase() +
  "-" +
  Math.random().toString(36).slice(2, 6).toUpperCase();

const SupplierAddProduct = () => {
  // Form state uses plain strings — converted to {en:...} on submit
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    category: "",
    subCategory: "",
    material: "",
    price: "",
    previous_price: "",
    stock: "",
    description: "",
    brand: "",
    height: "",
    weight: "",
    finishType: "",
    origin: "Made in India",
    careInstruction: "",
    return_policy: "7-day replacement for damages",
    gstPercentage: 18,
    shippingCharge: 150,
    measurement: "",
    features: [], // plain strings in UI; converted to [{en}] on submit
    isBestProduct: false,
  });

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [materials] = useState(["Brass", "Stone", "Marble", "Wood", "Bronze"]);
  const [mainImage, setMainImage] = useState(null);
  const [subImages, setSubImages] = useState([]);
  const [productVideo, setProductVideo] = useState(null); // ← was missing
  const [loading, setLoading] = useState(false);
  const [featuresInput, setFeaturesInput] = useState("");

  const navigate = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";

  // ── Fetch Categories ─────────────────────────────────────────────
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
        );
        setCategories(res.data || []);
      } catch {
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  // ── Fetch Subcategories ──────────────────────────────────────────
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!formData.category) {
        setSubCategories([]);
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
        );
        setSubCategories(
          res.data.filter((sub) => sub.category._id === formData.category)
        );
      } catch {
        toast.error("Failed to load subcategories");
      }
    };
    fetchSubCategories();
  }, [formData.category]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addFeature = () => {
    if (featuresInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featuresInput.trim()],
      }));
      setFeaturesInput("");
    }
  };

  const removeFeature = (index) =>
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));

  // ── Video handlers ────────────────────────────────────────────────
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a video file");
      return;
    }
    const toastId = toast.loading("Uploading video...");
    try {
      const upload = await uploadVideoToCloudinary(file);
      setProductVideo(upload.secure_url);
      toast.dismiss(toastId);
      toast.success("Video uploaded successfully!");
    } catch {
      toast.dismiss(toastId);
      toast.error("Video upload failed");
    }
  };

  const removeVideo = () => setProductVideo(null);

  // ── Submit: build correct multilingual payload ───────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mainImage) return toast.error("Main image is required!");
    if (!user?._id) return toast.error("Seller information not found!");

    setLoading(true);
    try {
      const productData = {
        bhkg_id: generateBhkgId(),
        sku: formData.sku,
        brand: formData.brand,
        height: formData.height,
        weight: formData.weight,
        price: Number(formData.price),
        previous_price: Number(formData.previous_price),
        stock: Number(formData.stock),
        stock_status: Number(formData.stock) > 0 ? "active" : "out of stock",
        gstPercentage: Number(formData.gstPercentage),
        shippingCharge: Number(formData.shippingCharge),
        isBestProduct: formData.isBestProduct,
        category: formData.category,
        subCategory: formData.subCategory,
        images: [mainImage, ...subImages],
        ratings: 4.8,
        uploadedBy: "supplier",
        supplier: user._id,
        automaticGrade: "Manual",
        controlSystem: "None",
        // ── Video: set is_special_product true so controller saves it ──
        ...(productVideo
          ? { productVideo, is_special_product: true }
          : { is_special_product: false }),

        // ── All multilingual fields wrapped with ml() ─────────────
        name:            ml(formData.name),
        description:     ml(formData.description),
        material:        ml(formData.material),
        finishType:      ml(formData.finishType),
        origin:          ml(formData.origin),
        careInstruction: ml(formData.careInstruction),
        return_policy:   ml(formData.return_policy),
        measurement:     ml(formData.measurement),
        productType:     ml("Religious Statue"),

        // features: array of plain strings → array of {en: string}
        features: formData.features.map(ml),

        // warranty.description is also multilingual
        warranty: {
          hasWarranty: true,
          warrantyDays: "30 days",
          description: ml("30-day warranty against manufacturing defects"),
        },

        // tradeInfo fields are all multilingual
        tradeInfo: {
          paymentTerms:       ml("Advance Payment (100%)"),
          deliveryTime:       ml("5-7 business days"),
          supplyAbility:      ml("500 pieces/month"),
          mainDomesticMarket: ml("All India"),
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/products/create-product`,
        productData
      );

      if (response.data?.message === "Product created successfully") {
        Swal.fire({
          title: "Success!",
          text: "Product added successfully.",
          icon: "success",
          confirmButtonColor: "#0e459e",
        });
        navigate("/seller/products");
      }
    } catch (error) {
      console.error("Create product error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Error creating product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row overflow-hidden">
      {/* ── LEFT: Image Upload ────────────────────────────────────── */}
      <div className="w-full lg:w-1/3 bg-white shadow-md p-6 overflow-y-auto lg:sticky lg:top-0 lg:h-screen">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate("/seller/products")}
            className="p-2 hover:bg-gray-200 rounded-lg transition"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-philosopher text-gray-700">Add Product</h1>
        </div>

        {/* Main Image */}
        <h2 className="text-lg font-philosopher mb-2">Main Image</h2>
        <label
          htmlFor="mainImageUpload"
          className="border-[#2275fc] overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center hover:bg-blue-50 transition"
        >
          <Upload className="text-[#2275fc] w-10 h-10 z-10" />
          <p className="z-10 text-xs sm:text-sm">
            Drop your image here or{" "}
            <span className="text-[#2275fc]">click to browse</span>
          </p>
          {mainImage && (
            <img
              src={mainImage}
              alt="Main Preview"
              className="absolute top-0 left-0 w-full h-full object-cover opacity-40"
            />
          )}
        </label>
        <input
          type="file"
          id="mainImageUpload"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const toastId = toast.loading("Uploading main image...");
            try {
              const upload = await uploadToCloudinary(file);
              setMainImage(upload.secure_url);
              toast.dismiss(toastId);
              toast.success("Main image uploaded!");
            } catch {
              toast.dismiss(toastId);
              toast.error("Failed to upload main image");
            }
          }}
        />
        {mainImage && (
          <div className="relative mt-3">
            <img
              src={mainImage}
              alt="Main"
              className="w-full h-48 object-cover rounded-lg border"
            />
            <button
              type="button"
              onClick={() => setMainImage(null)}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Sub Images */}
        <h2 className="text-lg font-philosopher mt-6 mb-2">Additional Images</h2>
        <label
          htmlFor="subImageUpload"
          className="border-[#2275fc] overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center hover:bg-blue-50 transition"
        >
          <Plus className="text-[#2275fc] w-8 h-8 z-10" />
          <p className="z-10 text-xs sm:text-sm">
            Drop your images here or{" "}
            <span className="text-[#2275fc]">click to browse</span>
          </p>
        </label>
        <input
          type="file"
          id="subImageUpload"
          multiple
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const files = Array.from(e.target.files);
            if (!files.length) return;
            const toastId = toast.loading("Uploading images...");
            try {
              const uploads = await Promise.all(files.map(uploadToCloudinary));
              setSubImages((prev) => [
                ...prev,
                ...uploads.map((u) => u.secure_url),
              ]);
              toast.dismiss(toastId);
              toast.success("Additional images uploaded!");
            } catch {
              toast.dismiss(toastId);
              toast.error("Failed to upload some images");
            }
          }}
        />
        {subImages.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            {subImages.map((img, i) => (
              <div key={i} className="relative">
                <img
                  src={img}
                  alt={`Sub ${i + 1}`}
                  className="w-full h-24 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={() =>
                    setSubImages((prev) => prev.filter((_, idx) => idx !== i))
                  }
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Video Upload ─────────────────────────────────────────── */}
        <h2 className="text-lg font-philosopher mt-6 mb-2">Product Video (Optional)</h2>
        <label
          htmlFor="videoUpload"
          className="border-[#2275fc] overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center hover:bg-blue-50 transition"
        >
          <Video className="text-[#2275fc] w-10 h-10 z-10" />
          <p className="z-10 text-xs sm:text-sm">
            {productVideo ? "Change video" : "Upload product video (MP4, WebM)"}
          </p>
        </label>
        <input
          type="file"
          id="videoUpload"
          accept="video/mp4,video/webm,video/quicktime"
          className="hidden"
          onChange={handleVideoUpload}
        />
        {productVideo ? (
          <div className="relative mt-3">
            <video src={productVideo} controls className="w-full h-40 rounded-lg border" />
            <button
              type="button"
              onClick={removeVideo}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 shadow"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-400 mt-1">Max 50MB. MP4 / WebM supported.</p>
        )}
      </div>

      {/* ── RIGHT: Form ───────────────────────────────────────────── */}
      <div className="w-full lg:w-2/3 overflow-y-auto h-auto lg:h-screen p-6 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Natraj Murti – The Cosmic Dance of Lord Shiva"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  English name required. Additional languages can be added by admin.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SKU <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  placeholder="e.g., NATRAJ-STN-001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {getText(cat.name, lang)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="subCategory"
                  value={formData.subCategory}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.category || subCategories.length === 0}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!formData.category
                      ? "Select category first"
                      : subCategories.length === 0
                      ? "No subcategories available"
                      : "Select Sub Category"}
                  </option>
                  {subCategories.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {getText(sub.name, lang)}
                    </option>
                  ))}
                </select>
                {formData.category && subCategories.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    No subcategories found for this category
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material <span className="text-red-500">*</span>
                </label>
                <select
                  name="material"
                  value={formData.material}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Material</option>
                  {materials.map((mat) => (
                    <option key={mat} value={mat}>{mat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  placeholder="Your brand name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Pricing & Stock</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price / MRP <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500">₹</span>
                  <input
                    type="number"
                    name="previous_price"
                    value={formData.previous_price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selling Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500">₹</span>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {formData.previous_price && formData.price && (
                <div className="md:col-span-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Discount: </strong>
                    {(
                      ((formData.previous_price - formData.price) /
                        formData.previous_price) *
                      100
                    ).toFixed(1)}
                    % off — Save ₹
                    {(formData.previous_price - formData.price).toFixed(2)}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  placeholder="e.g., 50"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  Stock &gt; 0 → active &nbsp;|&nbsp; Stock = 0 → out of stock
                </p>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                <input type="text" name="height" value={formData.height} onChange={handleInputChange}
                  placeholder="e.g., 12 inches"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
                <input type="text" name="weight" value={formData.weight} onChange={handleInputChange}
                  placeholder="e.g., 4 kg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Finish Type</label>
                <input type="text" name="finishType" value={formData.finishType} onChange={handleInputChange}
                  placeholder="e.g., Antique Polish"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Origin</label>
                <input type="text" name="origin" value={formData.origin} onChange={handleInputChange}
                  placeholder="e.g., Made in India"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Measurement</label>
                <input type="text" name="measurement" value={formData.measurement} onChange={handleInputChange}
                  placeholder="e.g., 12x8x4 inches"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Care Instructions</label>
                <input type="text" name="careInstruction" value={formData.careInstruction} onChange={handleInputChange}
                  placeholder="e.g., Wipe with dry cloth"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Return Policy</label>
                <input type="text" name="return_policy" value={formData.return_policy} onChange={handleInputChange}
                  placeholder="e.g., 7-day replacement for damages"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-lg font-philosopher mb-4">Features</h2>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={featuresInput}
                  onChange={(e) => setFeaturesInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                  placeholder="Add a product feature (press Enter or click +)"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button type="button" onClick={addFeature}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-bold">
                  +
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                    <span className="text-sm">{feature}</span>
                    <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {formData.features.length > 0 && (
                <p className="text-xs text-gray-400">
                  {formData.features.length} feature{formData.features.length > 1 ? "s" : ""} added
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Description</h2>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter detailed product description..."
              rows="6"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Additional Settings */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-philosopher mb-4">Additional Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Charge (₹)</label>
                <input type="number" name="shippingCharge" value={formData.shippingCharge}
                  onChange={handleInputChange} min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GST Percentage (%)</label>
                <input type="number" name="gstPercentage" value={formData.gstPercentage}
                  onChange={handleInputChange} min="0" max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input type="checkbox" name="isBestProduct" id="isBestProduct"
                  checked={formData.isBestProduct} onChange={handleInputChange}
                  className="w-4 h-4 accent-blue-600" />
                <label htmlFor="isBestProduct" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Mark as Best Product
                </label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pb-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#0e459e] text-white px-4 py-2 rounded-lg hover:bg-[#033485] transition flex items-center justify-center gap-2 font-medium disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {loading ? "Saving..." : "Save Product"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/seller/products")}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierAddProduct;